<template>
  <qx-dialog
    id="decrypt-file"
    ref="decryptDownload"
    :title="modalTitle"
    :buttons="actionButtons"
    :is-data-loading="isDataLoading"
    :in-progress="inProgress"
    type="small"
    @close="close">
    <template #body>
      <div ref="body">
        <qx-input-row
          id="password"
          v-model="passPhrase"
          :label="passphraseLabel"
          :disabled="decrypted"
          type="password"
          rules="required|password"
          name="passphrase" />
        <div>
          <label> <translate>Progress of decryption</translate>: </label>
          <qx-progress :progress="decryptProgress" />
        </div>
      </div>
    </template>
  </qx-dialog>
</template>

<script>
// Vuex
import { mapState, mapGetters, mapActions } from 'vuex';

// Elements
import QxDialog from '@/elements/QxDialog.vue';
import QxInputRow from '@/elements/QxInputRow.vue';
import qxProgress from '@/elements/progress.vue';
import EventBus from '@/elements/eventBus.js';

import { DecryptFile } from '@/libs/openpgp/handleDecrypt.js';

import { logErrorWithSentry } from '@/elements/utils.js';
import { prepareURL } from '@/api/httpModule';
import { getDownloadLink } from '@/api';

export default {
  name: 'DecryptDownload',
  components: {
    QxInputRow,
    QxDialog,
    qxProgress,
  },
  props: {
    selectedIds: {
      type: Array,
      default: () => [],
    },
    sharedItem: {
      type: Object,
      default: null,
    },
    downloadUrl: {
      type: String,
      default: '/file/download/',
    },
  },
  emits: ['close'],
  data() {
    return {
      isDataLoading: true,
      inProgress: false,
      decrypted: false,
      item: {},
      passPhrase: '',
      file: null,
      handle: undefined,
      decryptProgress: 0,
      passphraseLabel: this.$gettext('Enter passphrase'),
    };
  },
  computed: {
    ...mapState({
      profileId: ({ profile }) => profile.id,
      getPrivateKey: ({ pgpKeys }) => pgpKeys.private,
      shareMetadata: ({ downloadPage }) => downloadPage.metadata,
    }),
    ...mapGetters({
      getFileById: 'files/getFileById',
    }),
    privateKey() {
      return this.sharedItem?.privateKey || this.shareMetadata?.privateKey || this.getPrivateKey;
    },
    modalTitle() {
      return this.$gettext('Decrypt file');
    },
    actionButtons() {
      return {
        decrypt: {
          type: 'submit',
          state: 'active',
          title: this.$gettext('Decrypt & Download'),
          action: this.decryptAction,
          disabled: this.inProgress,
        },
        cancel: {
          type: 'cancel',
          state: 'passive',
          title: this.$gettext('Cancel'),
          isAlwaysEnable: true,
        },
      };
    },
    fileName() {
      const name = this.item.name;
      return name.slice(0, name.indexOf('.enc'));
    },
  },
  async created() {
    this.item = this.sharedItem?.item || this.getFileById(this.selectedIds[0]);
    try {
      if (!this.privateKey) {
        await this.getPgpKeys(this.profileId);
        if (!this.privateKey) {
          this.$notify({
            text: this.$gettext('Invalid private key'),
            type: 'error',
          });
        }
      }
    } catch (error) {
      if (error?.response) {
        this.$notify({
          text: this.$gettext('Cannot decrypt. You do not have PGP keys.'),
          type: 'error',
        });
      }
      logErrorWithSentry(error);
      this.close();
    } finally {
      this.isDataLoading = false;
    }
  },
  methods: {
    ...mapActions({
      getPgpKeys: 'pgpKeys/getUserKeys',
    }),
    async decryptAction() {
      try {
        if (this.inProgress) {
          return;
        }
        this.inProgress = true;
        this.actionButtons.decrypt.disabled = true;
        const { data } = this.sharedItem?.fetchLink
          ? await this.sharedItem.fetchLink(this.$route.params.id, [this.item.id])
          : await getDownloadLink({ ids: [this.item.id] });
        if (data?.id) {
          const downloadUrl = prepareURL(this.downloadUrl) + data.id;
          const fileSize = this.item.bytes;
          this.decryption({ downloadUrl, fileSize });
        }
      } catch (error) {
        this.inProgress = false;
        const msg =
          error.response?.data?.code === 90
            ? this.$gettext('You cannot download this folder because it is empty')
            : this.$gettext('File not found. Refresh file list and try again.');
        EventBus.$emit('handle-error', { response: { data: { msg } } });
        logErrorWithSentry(error);
      }
    },
    decryption({ downloadUrl, fileSize }) {
      this.handle = new DecryptFile({
        url: downloadUrl,
        size: fileSize,
        fileName: this.fileName,
        privateKey: this.privateKey,
        passPhrase: this.passPhrase,
        progress: ({ received, total }) => {
          this.decryptProgress = parseInt((received / total) * 100, 10);
        },
        finish: async ({ error, file }) => {
          if (error) {
            this.handle?.cancel?.();
            this.inProgress = false;
            this.decryptProgress = 0;
            this.actionButtons.decrypt.disabled = false;
            this.passPhrase = '';
            this.$notify({
              text: error,
              type: 'error',
            });
            return;
          }
          this.file = file;
          this.close();
        },
      });
    },
    // Dialog events
    close() {
      this.handle?.cancel?.();
      this.$emit('close', { file: this.file, fileName: this.fileName });
    },
  },
};
</script>

<style lang="scss" scoped>
.floatLabel {
  float: left;
  width: 140px;
}
</style>
